import { Fragment, useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, LogOut, RefreshCw, Lock, Phone, Trash2 } from 'lucide-react';
import { BRAND } from '@/content';
import ContentEditor from '@/components/ContentEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [user, setUser] = useState(null); // null=checking, false=logged out, obj=logged in
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [savingStatus, setSavingStatus] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [pw, setPw] = useState({ current: '', next: '' });
  const [pwMsg, setPwMsg] = useState('');
  const [digestMsg, setDigestMsg] = useState('');
  const [tab, setTab] = useState('leads');

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads`, { credentials: 'include' });
      if (res.status === 401) { setUser(false); return; }
      const data = await res.json();
      setLeads(data.leads || []);
    } catch {
      setError('Could not load leads. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (res.ok) {
          setUser(await res.json());
          loadLeads();
        } else {
          setUser(false);
        }
      } catch {
        setUser(false);
      }
    })();
  }, [loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data.detail;
        setError(typeof d === 'string' ? d : 'Login failed. Check your credentials.');
        return;
      }
      setUser(data);
      loadLeads();
    } catch {
      setError('Network error. Please try again.');
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    setUser(false);
    setLeads([]);
  };

  const exportCsv = async () => {
    const res = await fetch(`${API}/leads.csv`, { credentials: 'include' });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'metro-one-leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const setStatus = async (id, status) => {
    setSavingStatus(id);
    try {
      const res = await fetch(`${API}/leads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      if (res.ok) setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } finally {
      setSavingStatus('');
    }
  };

  const saveNote = async (id) => {
    const res = await fetch(`${API}/leads/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ notes: noteText }),
    });
    if (res.ok) {
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, notes: noteText } : l)));
      setEditingNote(null);
    }
  };

  const deleteLead = async (id, name) => {
    if (!window.confirm(`Delete the enquiry from ${name}? This cannot be undone.`)) return;
    const res = await fetch(`${API}/leads/${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) setLeads((ls) => ls.filter((l) => l.id !== id));
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg('');
    const res = await fetch(`${API}/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ current_password: pw.current, new_password: pw.next }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setPw({ current: '', next: '' });
      setPwMsg('ok:Password updated. Use it on your next sign-in.');
    } else {
      const d = data.detail;
      setPwMsg(`err:${typeof d === 'string' ? d : 'Could not update password (min 8 characters).'}`);
    }
  };

  const sendDigest = async () => {
    setDigestMsg('Sending…');
    const res = await fetch(`${API}/admin/send-digest`, { method: 'POST', credentials: 'include' });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      setDigestMsg(`Digest sent to the team inbox.${data.reminders ? ` Visit reminder sent for ${data.reminders} lead(s) due today.` : ''}`);
    } else {
      setDigestMsg('Could not send digest.');
    }
  };

  const filtered = filter === 'all' ? leads : leads.filter((l) => (l.status || 'new') === filter);

  const today = new Date().toDateString();
  const todayCount = leads.filter((l) => new Date(l.submitted_at).toDateString() === today).length;

  if (user === null) {
    return <div className="flex min-h-[100dvh] items-center justify-center bg-[#FAF9F6] text-sm text-[#646A72]">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#FAF9F6] px-4">
        <motion.form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-[#E4E6EB] bg-white p-8 shadow-[0_30px_60px_-30px_rgba(28,29,33,0.3)]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <img src={BRAND.logo} alt="Codename Metro One — Navade" className="h-9 w-auto" />
          <h1 className="mt-5 font-display text-xl font-extrabold uppercase tracking-tight text-[#1C1D21]">Sales Dashboard</h1>
          <p className="mt-1 text-xs text-[#646A72]">Authorised team access only.</p>
          <label htmlFor="admin-email" className="mt-6 block text-sm font-bold text-[#1C1D21]">Email</label>
          <input
            id="admin-email" data-testid="admin-email-input" type="email" autoComplete="username" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-[#E4E6EB] bg-[#FAF9F6] px-4 py-3 text-base outline-none focus:border-[#C92F4B]"
          />
          <label htmlFor="admin-password" className="mt-4 block text-sm font-bold text-[#1C1D21]">Password</label>
          <input
            id="admin-password" data-testid="admin-password-input" type="password" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-[#E4E6EB] bg-[#FAF9F6] px-4 py-3 text-base outline-none focus:border-[#C92F4B]"
          />
          {error && <p data-testid="admin-login-error" className="mt-3 text-xs font-semibold text-[#A41225]">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" className="btn-primary mt-6 w-full">
            <Lock size={15} /> Sign In
          </button>
          <a href="/" className="mt-4 block text-center text-xs font-semibold text-[#646A72] hover:text-[#C92F4B]">← Back to microsite</a>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#FAF9F6]">
      <header className="sticky top-0 z-10 border-b border-[#E4E6EB] bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Metro One" className="h-8 w-auto" />
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-[#404145]">Leads Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh-btn" onClick={loadLeads} aria-label="Refresh leads" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB] bg-white">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button data-testid="admin-export-csv-btn" onClick={exportCsv} className="btn-primary !px-5 !py-2.5 !text-[11px]">
              <Download size={15} /> Export CSV
            </button>
            <a
              data-testid="admin-handout-btn"
              href="/metro-one-visit-handout.pdf"
              download="Metro-One-Visit-Handout.pdf"
              className="flex h-10 items-center gap-2 rounded-full border border-[#E4E6EB] bg-white px-5 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-[#404145] transition-colors hover:border-[#C92F4B] hover:text-[#C92F4B]"
            >
              <Download size={15} /> Visit Handout
            </a>
            <button data-testid="admin-logout-btn" onClick={logout} aria-label="Log out" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB] bg-white">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="border-b border-[#E4E6EB] bg-white/60">
        <div className="mx-auto flex max-w-6xl gap-2 px-4 py-2.5 sm:px-6" role="tablist" aria-label="Dashboard sections">
          {[['leads', 'Leads'], ['content', 'Website Content']].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`rounded-full px-5 py-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                tab === id ? 'bg-[#1C1D21] text-white' : 'text-[#646A72] hover:text-[#C92F4B]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'content' ? (
        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-content-panel">
          <ContentEditor />
        </main>
      ) : (
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-dashboard">
        <div className="grid grid-cols-3 gap-3">
          {[
            ['Total Enquiries', leads.length],
            ['Today', todayCount],
            ['2 BHK Interest', leads.filter((l) => l.configuration === '2 BHK').length],
          ].map(([label, val]) => (
            <div key={label} className="rounded-2xl border border-[#E4E6EB] bg-white p-4 sm:p-6">
              <p className="font-display text-2xl font-extrabold text-[#1C1D21] sm:text-4xl" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{val}</p>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#646A72] sm:text-xs">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Filter leads by status">
          {[['all', 'All'], ['new', 'New'], ['contacted', 'Contacted'], ['visited', 'Visited']].map(([id, label]) => (
            <button
              key={id}
              data-testid={`filter-${id}-btn`}
              role="tab"
              aria-selected={filter === id}
              onClick={() => setFilter(id)}
              className={`min-h-[44px] rounded-full border px-5 text-xs font-bold uppercase tracking-[0.12em] transition-colors ${
                filter === id ? 'border-[#C92F4B] bg-[#C92F4B] text-white' : 'border-[#E4E6EB] bg-white text-[#404145]'
              }`}
            >
              {label} ({id === 'all' ? leads.length : leads.filter((l) => (l.status || 'new') === id).length})
            </button>
          ))}
        </div>

        <div className="mt-3 overflow-hidden rounded-2xl border border-[#E4E6EB] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-[#E4E6EB] bg-[#FAF9F6] text-[10px] font-bold uppercase tracking-[0.16em] text-[#646A72]">
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Config</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Timeline</th>
                  <th className="px-4 py-3">Visit</th>
                  <th className="px-4 py-3">Source / UTM</th>
                  <th className="px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4E6EB]">
                {filtered.map((l, i) => (
                  <Fragment key={l.id || i}>
                  <tr className="transition-colors hover:bg-[#FAF9F6]">
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-[#646A72]">
                      {l.submitted_at ? new Date(l.submitted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td className="px-4 py-3 font-bold text-[#1C1D21]">{l.name}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <a href={`tel:+91${l.mobile}`} className="inline-flex items-center gap-1 font-semibold text-[#C92F4B]">
                          <Phone size={12} /> {l.mobile}
                        </a>
                        <a
                          data-testid={`whatsapp-lead-${l.id || i}`}
                          href={`https://wa.me/91${l.mobile}?text=${encodeURIComponent(`Hi ${l.name}, this is the Metro One Navade team regarding your site-visit enquiry for ${l.configuration}. `)}`}
                          target="_blank" rel="noopener noreferrer"
                          aria-label={`WhatsApp ${l.name}`}
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366]/12 text-[#128C4B] transition-colors hover:bg-[#25D366]/25"
                        >
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
                            <path d="M12.04 2a9.9 9.9 0 0 0-8.5 14.96L2 22l5.2-1.5A9.9 9.9 0 1 0 12.04 2Zm5.77 14.1c-.24.68-1.4 1.3-1.96 1.35-.5.05-1.13.24-3.8-.8-3.2-1.26-5.24-4.53-5.4-4.74-.16-.21-1.3-1.72-1.3-3.28 0-1.56.82-2.33 1.11-2.65.29-.32.64-.4.85-.4h.61c.2 0 .46-.07.72.55.27.63.9 2.18.98 2.34.08.16.13.35.02.56-.1.21-.16.34-.32.53-.16.19-.34.42-.48.56-.16.16-.33.34-.14.66.18.32.82 1.35 1.76 2.19 1.21 1.08 2.23 1.41 2.55 1.57.32.16.5.13.69-.08.19-.21.79-.92 1-1.24.21-.32.42-.27.71-.16.29.1 1.85.87 2.17 1.03.32.16.53.24.61.37.08.14.08.79-.16 1.47Z" />
                          </svg>
                        </a>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-[#C92F4B]/8 px-2.5 py-1 text-xs font-bold text-[#A41225]">{l.configuration}</span>
                      {l.furnishing && <span className="ml-1.5 rounded-full bg-[#404145]/8 px-2.5 py-1 text-[10px] font-bold text-[#404145]">{l.furnishing}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1" role="group" aria-label={`Status for ${l.name}`}>
                        {[['new', 'New'], ['contacted', 'Contacted'], ['visited', 'Visited']].map(([s, label]) => {
                          const on = (l.status || 'new') === s;
                          return (
                            <button
                              key={s}
                              data-testid={`status-${s}-${l.id || i}`}
                              aria-pressed={on}
                              disabled={savingStatus === l.id}
                              onClick={() => setStatus(l.id, s)}
                              className={`min-h-[36px] rounded-full border px-3 text-[10px] font-bold uppercase tracking-[0.08em] transition-colors disabled:opacity-50 ${
                                on
                                  ? s === 'visited'
                                    ? 'border-[#15803D] bg-[#15803D] text-white'
                                    : s === 'contacted'
                                      ? 'border-[#B45309] bg-[#B45309] text-white'
                                      : 'border-[#C92F4B] bg-[#C92F4B] text-white'
                                  : 'border-[#E4E6EB] bg-white text-[#646A72] hover:text-[#404145]'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#404145]">{l.timeline}</td>
                    <td className="px-4 py-3 text-xs text-[#404145]">{l.visit_date || 'Flexible'} {l.visit_time ? `· ${l.visit_time.split(' ')[0]}` : ''}</td>
                    <td className="px-4 py-3 text-xs text-[#646A72]">{l.utm?.utm_source || l.source_page || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          data-testid={`note-toggle-${l.id || i}`}
                          onClick={() => { setEditingNote(editingNote === (l.id || i) ? null : (l.id || i)); setNoteText(l.notes || ''); }}
                          title={l.notes || 'Add follow-up note'}
                          aria-label={`Notes for ${l.name}`}
                          className={`min-h-[36px] max-w-[150px] truncate rounded-full border px-3 text-left text-[10px] font-bold transition-colors ${
                            l.notes ? 'border-[#C92F4B]/40 text-[#A41225]' : 'border-[#E4E6EB] text-[#646A72] hover:text-[#404145]'
                          }`}
                        >
                          {l.notes || '+ Note'}
                        </button>
                        <button
                          data-testid={`delete-lead-${l.id || i}`}
                          onClick={() => deleteLead(l.id, l.name)}
                          aria-label={`Delete enquiry from ${l.name}`}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E4E6EB] text-[#646A72] transition-colors hover:border-[#A41225] hover:text-[#A41225]"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {editingNote === (l.id || i) && (
                    <tr className="bg-[#FAF9F6]">
                      <td colSpan={9} className="px-4 py-4">
                        <label htmlFor={`note-editor-${l.id || i}`} className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#646A72]">
                          Follow-up notes for {l.name}
                        </label>
                        <textarea
                          id={`note-editor-${l.id || i}`}
                          data-testid={`note-editor-${l.id || i}`}
                          rows={2}
                          maxLength={500}
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Call outcome, next steps, callback time…"
                          className="mt-1.5 w-full rounded-xl border border-[#E4E6EB] bg-white px-3 py-2 text-sm outline-none focus:border-[#C92F4B]"
                        />
                        <div className="mt-2 flex gap-2">
                          <button data-testid={`note-save-${l.id || i}`} onClick={() => saveNote(l.id)} className="btn-primary !min-h-[40px] !px-5 !py-2 !text-[10px]">Save Note</button>
                          <button data-testid={`note-cancel-${l.id || i}`} onClick={() => setEditingNote(null)} className="btn-ghost !min-h-[40px] !px-5 !py-2 !text-[10px]">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-[#646A72]" data-testid="admin-empty-state">No enquiries in this view — new site-visit bookings will appear here.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <form onSubmit={changePassword} data-testid="change-password-form" className="rounded-2xl border border-[#E4E6EB] bg-white p-6">
            <h2 className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#1C1D21]">Change Password</h2>
            <label htmlFor="pw-current" className="mt-4 block text-xs font-bold text-[#404145]">Current password</label>
            <input
              id="pw-current" data-testid="pw-current-input" type="password" autoComplete="current-password" required
              value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
              className="mt-1.5 w-full rounded-xl border border-[#E4E6EB] bg-[#FAF9F6] px-4 py-2.5 text-sm outline-none focus:border-[#C92F4B]"
            />
            <label htmlFor="pw-new" className="mt-3 block text-xs font-bold text-[#404145]">New password (min 8 characters)</label>
            <input
              id="pw-new" data-testid="pw-new-input" type="password" autoComplete="new-password" required minLength={8}
              value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
              className="mt-1.5 w-full rounded-xl border border-[#E4E6EB] bg-[#FAF9F6] px-4 py-2.5 text-sm outline-none focus:border-[#C92F4B]"
            />
            {pwMsg && (
              <p data-testid="pw-message" className={`mt-3 text-xs font-semibold ${pwMsg.startsWith('ok:') ? 'text-[#15803D]' : 'text-[#A41225]'}`}>
                {pwMsg.slice(3)}
              </p>
            )}
            <button data-testid="pw-update-btn" type="submit" className="btn-primary mt-4 !px-6 !py-2.5 !text-[11px]">Update Password</button>
          </form>

          <div className="rounded-2xl border border-[#E4E6EB] bg-white p-6">
            <h2 className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#1C1D21]">Daily Lead Digest</h2>
            <p className="mt-3 text-xs leading-relaxed text-[#404145]">
              Every morning at 8:00 AM IST, a summary of the last 24 hours of enquiries is emailed to the team inbox automatically — plus a reminder for any site visits booked for today that are still marked New.
            </p>
            <button data-testid="send-digest-btn" onClick={sendDigest} className="btn-ghost mt-4 !px-6 !py-2.5 !text-[11px]">
              Send Digest Now
            </button>
            {digestMsg && <p data-testid="digest-message" className="mt-3 text-xs font-semibold text-[#404145]">{digestMsg}</p>}
          </div>
        </div>

        <p className="mt-4 text-xs text-[#646A72]">Logged in as {user.email}. Session expires after 30 minutes of inactivity.</p>
      </main>
      )}
    </div>
  );
}
